import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const PaymentModal = ({ 
  isOpen = false, 
  onClose = () => {}, 
  featureName = 'Premium Feature',
  featureDescription = 'Unlock advanced analytics and insights',
  onSuccess = () => {}
}) => {
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: '₹2,499',
      period: '/month',
      features: ['All premium features', 'Unlimited reports', 'Priority support', 'Advanced AI insights']
    },
    {
      id: 'annual',
      name: 'Annual',
      price: '₹24,999',
      period: '/year',
      savings: 'Save ₹4,989',
      features: ['All premium features', 'Unlimited reports', 'Priority support', 'Advanced AI insights', '2 months free']
    }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsProcessing(true);

    setTimeout(() => {
      setIsProcessing(false);
      onSuccess();
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl bg-card rounded-xl elevation-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">
              Upgrade to Premium
            </h2>
            <p className="text-sm text-muted-foreground font-body mt-1">
              {featureName} requires a premium subscription
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={isProcessing}
            className="p-2 rounded-md hover:bg-muted transition-smooth disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 p-4 bg-accent/10 border border-accent/20 rounded-md">
            <div className="flex items-start gap-3">
              <Icon name="Sparkles" size={20} color="var(--color-accent)" />
              <div>
                <p className="text-sm font-medium text-foreground font-body">
                  {featureDescription}
                </p>
                <p className="text-xs text-muted-foreground font-caption mt-1">
                  Get instant access to all premium features with your subscription
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {plans?.map((plan) => (
              <button
                key={plan?.id}
                onClick={() => setSelectedPlan(plan?.id)}
                className={`relative p-6 rounded-md border-2 transition-smooth text-left ${
                  selectedPlan === plan?.id
                    ? 'border-primary bg-primary/5 elevation-md' :'border-border hover:border-primary/50 hover:elevation-sm'
                }`}
              >
                {plan?.savings && (
                  <div className="absolute -top-2 right-4 px-2 py-1 bg-success text-success-foreground text-xs font-medium font-caption rounded">
                    {plan?.savings}
                  </div>
                )}
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-2xl font-heading font-bold text-foreground">
                    {plan?.price}
                  </span>
                  <span className="text-sm text-muted-foreground font-body">
                    {plan?.period}
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground font-body mb-3">
                  {plan?.name} Plan
                </p>
                <ul className="space-y-2">
                  {plan?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-xs text-muted-foreground font-body">
                      <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Card Number"
              type="text"
              placeholder="1234 5678 9012 3456"
              value={cardNumber}
              onChange={(e) => setCardNumber(e?.target?.value)}
              required
              disabled={isProcessing}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Expiry Date"
                type="text"
                placeholder="MM/YY"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e?.target?.value)}
                required
                disabled={isProcessing}
              />
              <Input
                label="CVV"
                type="text"
                placeholder="123"
                value={cvv}
                onChange={(e) => setCvv(e?.target?.value)}
                required
                disabled={isProcessing}
              />
            </div>

            <div className="pt-4 flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={handleClose}
                disabled={isProcessing}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isProcessing}
                iconName="Lock"
                iconPosition="left"
                fullWidth
              >
                {isProcessing ? 'Processing...' : 'Subscribe Now'}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground font-caption">
              Your payment is secure and encrypted. Cancel anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;